const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

function currentAdminUserId() {
  // The super admin's real id (root id when impersonating).
  return localStorage.getItem('userId1') || localStorage.getItem('userId');
}

/**
 * Loads the demo feature catalog joined with the stored access choice.
 * Returns [{ featureKey, label, category, type, access, accessCode }].
 */
export async function fetchDemoFeatures() {
  const adminUserId = currentAdminUserId();
  const response = await fetch(
    `${apiUrl}/api/demo/features?adminUserId=${encodeURIComponent(adminUserId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        Authorization: getBasicAuthHeader(),
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Demo features request failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Updates a single feature's access. `access` is the int code:
 * 0 = hidden, 1 = visible/allow, 2 = disabled/read-only.
 */
export async function updateDemoFeature(featureKey, access) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(`${apiUrl}/api/demo/features`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ adminUserId, featureKey, access }),
  });
  if (!response.ok) {
    let message = `Update failed: ${response.status}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }
  return response.json();
}

/**
 * Bulk-updates access for many features. Accepts either:
 * - updates: [{ featureKey, access }, ...]
 * - featureKeys: [...], access: N
 */
export async function bulkUpdateDemoFeatures({ updates, featureKeys, access }) {
  const adminUserId = currentAdminUserId();
  const body = { adminUserId };
  if (Array.isArray(updates) && updates.length > 0) {
    body.updates = updates;
  } else if (Array.isArray(featureKeys) && featureKeys.length > 0 && access != null) {
    body.featureKeys = featureKeys;
    body.access = access;
  } else {
    throw new Error('Provide updates[] or featureKeys[] with access.');
  }

  const response = await fetch(`${apiUrl}/api/demo/features/bulk`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let message = `Bulk update failed: ${response.status}`;
    try {
      const payload = await response.json();
      if (payload?.message) message = payload.message;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }
  return response.json();
}

/**
 * Flags (or unflags) a target account as a read-only demo user (super admin only).
 * `isDemoUser` is the int flag: 1 = demo user, 0 = normal user.
 */
export async function updateUserDemoFlag(targetUserId, isDemoUser) {
  const adminUserId = currentAdminUserId();
  const response = await fetch(`${apiUrl}/api/demo/user-flag`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({
      adminUserId,
      targetUserId,
      isDemoUser: isDemoUser === 1 ? 1 : 0,
    }),
  });
  if (!response.ok) {
    let message = `Update failed: ${response.status}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch (_) {
      /* ignore */
    }
    throw new Error(message);
  }
  return response.json();
}
